<template>
  <div
    class="container p-0 hm-min-height-medium"
    :class="{ 'media-form-modal--lg': showSelectMedia }"
  >
    <HMModalHeader :title="$t(titleKey)" @close="$emit('close')" />
    <div v-if="!formModel.media" class="upload">
      <HMTabs v-model="uploadType">
        <HMTab name="media-library" v-if="showSelectMedia">
          {{ $t('MEDIA_LIBRARY.LABEL.MEDIA-LIBRARY') }}
        </HMTab>
        <HMTab name="upload-file" v-if="showUploadMedia">
          {{ $t('MEDIA_LIBRARY.LABEL.UPLOAD-FILE') }}
        </HMTab>
        <HMTab name="embed-video" v-if="showEmbedMedia">
          {{ $t('MEDIA_LIBRARY.LABEL.EMBED') }}
        </HMTab>
        <HMTab name="embed-html" v-if="showEmbedHtml">
          {{ $t('MEDIA_LIBRARY.LABEL.TEXT_HTML') }}
        </HMTab>
      </HMTabs>
      <div
        class="mt-3 pb-5 pb-sm-0 force-width"
        v-show="uploadType === 'media-library'"
        v-if="showSelectMedia"
      >
        <!-- using v-show & v-if for performance -->
        <MediaSearchSection
          class="pt-2"
          v-model="searchQuery"
          :allowedTypes="allowedTypes"
          :selectedType="selectedType"
        />
        <HMPaginatedTable
          ref="media-form-table"
          api="mediaLibrary/getMedia"
          :query="searchQuery"
          name="media-form-table"
        >
          <template slot-scope="{ data }">
            <div class="mt-4 row">
              <div
                class="column col-md-4"
                v-for="mediaItem in data.res.results"
                :key="mediaItem.id"
              >
                <MediaItemCard
                  :animated="false"
                  :isSelectable="
                    canEnableSelection || isMediaItemsSelected(mediaItem)
                  "
                  @selectionChanged="toggleMediaItem(mediaItem)"
                  :media="mediaItem"
                  :active="isMediaItemsSelected(mediaItem)"
                />
              </div>
            </div>
          </template>
        </HMPaginatedTable>
        <HMModalFooter
          class="mt-3"
          :submitButton="$t('MEDIA_LIBRARY.LABEL.EMBED')"
          @close="$emit('close')"
        >
          <template slot="submit">
            <HMButton
              color="primary"
              @click="mediaSelected"
              :loading="mediaLoading"
            >
              {{
                showSelectMedia
                  ? $t('GENERAL.BUTTON.SELECT')
                  : $t('GENERAL.BUTTON.SAVE')
              }}
            </HMButton>
          </template>
        </HMModalFooter>
      </div>
      <HMFileUpload
        v-if="uploadType === 'upload-file'"
        class="my-4"
        @input="submitMedia"
        :title="$t('MEDIA_LIBRARY.TITLE.UPLOAD_NEW_FILE')"
        :description="$t('MEDIA_LIBRARY.TEXT.DRAG_AND_DROP_FILE_HERE')"
        :videoUploadAllowed="videoUploadAllowed"
      />
      <div v-if="uploadType === 'embed-video'">
        <ValidationObserver ref="form" v-slot="{ invalid }">
          <form class="mt-3" @submit.prevent="embedVideo">
            <HMTextInput
              class="mb-2"
              type="text"
              rules="required"
              :label="$t('MEDIA_LIBRARY.LABEL.URL_LINK')"
              v-model="embedFileUrl"
              :name="$t('MEDIA_LIBRARY.LABEL.URL_LINK')"
            />
            <span class="small">
              <i class="far fa-info-circle mr-2"></i>
              {{ $t('MEDIA_LIBRARY.TEXT.SUPPORTED_EMBEDS') }}
            </span>
            <HMModalFooter
              :submitButton="$t('MEDIA_LIBRARY.LABEL.EMBED')"
              :disabled="invalid || inProgress"
              :loading="inProgress"
              :formError="formError"
              @close="$emit('close')"
            >
              <template v-slot:close>
                <div></div>
              </template>
            </HMModalFooter>
          </form>
        </ValidationObserver>
      </div>
      <div v-if="uploadType === 'embed-html'">
        <ValidationObserver ref="form" v-slot="{ invalid }">
          <form class="mt-3" @submit.prevent="embedHtml">
            <div
              class="d-flex flex-row align-items-center justify-content-between mb-3"
            >
              <label class="c-charcoal m-0 mr-4">
                {{ $t('MEDIA_LIBRARY.LABEL.RAW_HTML') }}
              </label>
              <HMSwitch v-model="embedContentRaw" />
            </div>
            <HMTextarea
              class="w-100"
              v-if="embedContentRaw"
              v-model="embedContent"
              :maxLength="65535"
              :rows="15"
              :minHeight="'300px'"
              placeholder="Your html content"
            ></HMTextarea>
            <HMVueEditor
              v-else
              id="hm-vue-embed-content-editor"
              class="w-100 mb-2"
              v-model="embedContent"
              placeholder="Your rich text content"
            />
            <HMModalFooter
              :submitButton="$t('MEDIA_LIBRARY.BUTTON.SAVE')"
              :disabled="invalid || inProgress"
              :loading="inProgress"
              :formError="formError"
              @close="$emit('close')"
            >
              <template v-slot:close>
                <div></div>
              </template>
            </HMModalFooter>
          </form>
        </ValidationObserver>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import { get, cloneDeep } from 'lodash';

import MediaItemCard from '@/web/components/media-library/MediaItemCard.vue';
import MediaSearchSection from '@/web/components/media-library/MediaSearchSection.vue';
import { embedFileHtml, embedFileUrl } from '@/web/services/upload';
import HMVueEditor from '@/web/components/common/HMVueEditor';
import HMTextarea from '@/web/components/common/HMTextarea';

export default {
  name: 'MediaFormModal',
  components: {
    HMTextarea,
    ValidationObserver,
    MediaItemCard,
    MediaSearchSection,
    HMVueEditor,
  },
  props: {
    media: {
      type: Object,
      default() {
        return {
          id: null,
          title: null,
          media: null,
          instructor: null,
          options: [],
          description: null,
        };
      },
    },
    showEditMedia: {
      type: Boolean,
      default: true,
    },
    refreshMedia: {
      type: Function,
      default: () => Promise.resolve(),
    },
    showSelectMedia: {
      type: Boolean,
      default: false,
    },
    showUploadMedia: {
      type: Boolean,
      default: true,
    },
    showEmbedMedia: {
      type: Boolean,
      default: true,
    },
    showEmbedHtml: {
      type: Boolean,
      default: true,
    },
    mediaLoading: {
      type: Boolean,
      default: false,
    },
    multiSelect: {
      type: Boolean,
      default: true,
    },
    videoUploadAllowed: {
      type: Boolean,
      default: true,
    },
    justFile: {
      type: Boolean,
      default: false,
    },
    existingContent: {
      type: String,
      default: null,
    },
    existingContentRaw: {
      type: Boolean,
      default: false,
    },
    allowedTypes: {
      type: Array,
      default: () => ['application', 'video', 'audio'],
    },
    selectedType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      embedFileUrl: null,
      embedContent: null,
      embedContentRaw: false,
      formModel: {},
      formError: null,
      inProgress: false,
      uploadType: 'upload-file',
      searchQuery: {},
      mediaItemsSelected: [],
    };
  },
  computed: {
    titleKey() {
      return this.media.id
        ? 'MEDIA_LIBRARY.TITLE.EDIT_FILE'
        : 'MEDIA_LIBRARY.TITLE.ADD_FILE';
    },
    numMediaItemsSelected() {
      return this.mediaItemsSelected.length;
    },
    canEnableSelection() {
      if (this.multiSelect) return true;
      return this.numMediaItemsSelected === 0;
    },
  },
  methods: {
    formModelToPostData(formModel) {
      const data = { ...formModel };
      data.title = formModel.title;
      data.media = formModel.media.id;
      data.instructor = formModel.instructor?.id;
      data.options = formModel.options.map(option => option.id);
      return data;
    },
    mediaModelToFormModel(media) {
      const formModel = cloneDeep(media);
      formModel.options = formModel.options.map(
        ({ option_id, category_name, option_name }) => ({
          id: option_id,
          name: `${category_name} - ${option_name}`,
        })
      );
      return formModel;
    },
    async submitMedia(mediaFile) {
      if (this.justFile) {
        this.$parent.$emit('file-added', mediaFile);
        this.$emit('close');
        return;
      }
      this.inProgress = true;
      this.formModel.media = mediaFile;
      this.formModel.title = mediaFile.file_name?.split('.')[0];

      const data = this.formModelToPostData(this.formModel);

      try {
        const newMedia = await this.$store.dispatch(
          'mediaLibrary/createMedia',
          data
        );
        if (this.showEditMedia) {
          const params = { media_id: newMedia.id };
          this.$router.push({ name: 'media-library-edit-item', params });
        } else {
          this.$parent.$emit('media-added', newMedia);
        }
        this.$emit('close');
      } catch (error) {
        this.formError = get(error, 'response.data.non_field_errors[0]', null);
        this.inProgress = false;
      }
    },
    isMediaItemsSelected(id) {
      return this.mediaItemsSelected.indexOf(id) > -1;
    },
    toggleMediaItem(mediaItemId) {
      const index = this.mediaItemsSelected.indexOf(mediaItemId);
      if (index === -1) {
        this.mediaItemsSelected.push(mediaItemId);
      } else {
        this.mediaItemsSelected.splice(index, 1);
      }
    },
    mediaSelected() {
      this.$parent.$emit('media-selected', this.mediaItemsSelected);
      this.$emit('close');
    },
    async embedVideo() {
      this.inProgress = true;

      try {
        const media = await embedFileUrl(this.embedFileUrl);
        this.submitMedia(media);
      } catch (error) {
        this.formError = get(error, 'response.data.non_field_errors[0]', null);
      }

      this.inProgress = false;
    },
    async embedHtml() {
      this.inProgress = true;

      try {
        const media = await embedFileHtml({
          content: this.embedContent,
          contentRaw: this.embedContentRaw,
        });
        this.submitMedia(media);
      } catch (error) {
        this.formError = get(error, 'response.data.non_field_errors[0]', null);
      }

      this.inProgress = false;
    },
  },
  mounted() {
    this.formModel = this.mediaModelToFormModel(this.media);
    this.embedContent = this.existingContent;
    this.embedContentRaw = this.existingContentRaw;
    this.uploadType = this.showSelectMedia ? 'media-library' : 'upload-file';

    this.$trackOnPage('teacher open select media');
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.media-form-modal--lg {
  @include media-breakpoint-up(lg) {
    width: 900px;
  }
}

.file-upload {
  background-color: $light-gray;
  border: 2px dashed $medium-gray;
  margin: 1.5rem 0 1rem;
  padding: 3rem 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  border-radius: 6px;
  text-align: center;
}

.thumbnail {
  background-color: $medium-gray;
  height: 130px;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-size: cover;
}

.thumbnail-icon {
  font-size: 2rem;
}

.info-table td {
  padding: 0;
}
.file-upload-empty-image {
  width: 250px;
  height: 200px;
  max-width: 100%;
}
.max-width-small {
  max-width: 350px;
}
.hm-min-height-medium {
  min-height: 650px;
}
</style>
